import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFlag } from "@innovid/feature-flags-react";

import { Switch } from "react-router";
import { Route } from "react-router-dom";

import { hasActiveFilters } from "reducers/common/ColumnFilters";
import { loadLibrary, resetForm } from "reducers/LibraryProperties";
import {
	resetSocialInfo,
	loadDSPConnections,
	loadDSPMetadata,
	loadCTAs,
	loadGoogleLocales
} from "reducers/Social";
import { AdvertiserDetails } from "reducers/AdvertiserDetails";
import { Labels } from "reducers/Labels";

import { FACEBOOK_DSP_INTERNAL_NAME } from "utils/constants";
import { GOOGLE_DSP_INTERNAL_NAME } from "widgets/social2/constants/common";
import { FEATURE_FLAGS } from "featureFlag/featureFlag";

import CreativeGrid from "./creatives/CreativeGrid";
import ApprovalHistoryModal from "widgets/modals/creatives/ApprovalHistory";
import ImportSocialCreativesModal from "widgets/modals/creatives/bulk/ImportSocialCreativesModal";
import CreativesInfoHeader from "widgets/toolbars/CreativesInfoHeader";
import { buildSelectedCreativesList } from 'selectors/Creatives';
import { isLibraryLoadedForId } from "selectors/LibraryProperties";
import AssetList from "./AssetList";
import PreviewGrid from "./previews/PreviewGrid";
import QrCodesDesignForm from "routes/qrcodes/qr_design/QrCodesDesignForm";
import QrCodesDesignsGrid from "./qrcodes/QrCodesDesignsGrid";
import QrCodesGrid from "./qrcodes/QrCodesGrid";
import CreateContentFeedForm from "./contentfeeds/CreateContentFeedForm";
import AssignContentFeedForm from "./contentfeeds/AssignContentFeedForm";
import AssignBlankContentFeedForm from "./contentfeeds/AssignBlankContentFeedForm";
import ContentFeedList from "./contentfeeds/ContentFeedList";
import UpdatesHistoryPage from "./contentfeeds/UpdatesHistoryPage";
import useCancelToken from "hooks/useCancelToken";

import "./CreativesList.css";

export default function CreativesList({ libraryId }) {
	const dispatch = useDispatch()
		, { creatives
			, dspConnections
			, libraryProperties
			,
		} = useSelector((state) => {
			return {
				creatives: buildSelectedCreativesList(state),
				dspConnections:state.social.items,
				libraryProperties: state.libraryProperties,
			};
		})
		, advertiserId = libraryProperties?.advertiser?.id;

	const isLibraryLoaded = useSelector(state => isLibraryLoadedForId(state, libraryId));

	const isContentFeedAssignmentFlowsEnabled = useFlag(FEATURE_FLAGS.CONTENT_FEED_ASSIGNMENT_FLOWS);
	const isContentFeedsTabEnabled = useFlag(FEATURE_FLAGS.CONTENT_FEEDS_TAB_FEATURE_FLAG);

	let hasFiltersAppliedClass = hasActiveFilters(creatives) ? "has-filters-applied" : "";

	const { getNewToken, cancel } = useCancelToken(true);

	useEffect(() => {
		dispatch(resetSocialInfo());
		dispatch(loadLibrary(libraryId, getNewToken()));
		dispatch(loadDSPConnections(libraryId));
		return cancel;
	}, [libraryId]);

	useEffect(() => {
		if (isLibraryLoaded && advertiserId) {
			dispatch(AdvertiserDetails.loadAdvertiserDetails(libraryId));
		}
	}, [isLibraryLoaded, advertiserId, libraryId]);

	useEffect(() => {
		// Only load the CTA list if there are DSP connections
		if(dspConnections.length){
			dispatch(loadCTAs());
		}
		try {
			dspConnections.forEach((val) => {
				let { id, internalName } = val?.dsp || {};
				if(internalName === FACEBOOK_DSP_INTERNAL_NAME){
					dispatch(loadDSPMetadata(id));
				}
				if(internalName === GOOGLE_DSP_INTERNAL_NAME){
					dispatch(loadGoogleLocales());
				}
			});
		} catch(err){}
	}, [dspConnections]);

	useEffect(() => {
		// advertiser id is initially not in the libraryProperties obj on load
		if(advertiserId) {
			dispatch(Labels.loadAdvertiserTaxonomy(advertiserId));
		}
	}, [advertiserId]);

	useEffect(() => {
		return () => {
			dispatch(resetForm());
			dispatch(AdvertiserDetails.resetDetails());
			dispatch(Labels.clearLabels());
		};
	}, []);

	return (
		<div className={`creatives-list ${hasFiltersAppliedClass}`}>
			<CreativesInfoHeader type="library" libraryId={libraryId} />
			<Switch>
				<Route
					path   = {`/library/:libraryId/assets/`}
					// We need `routeRenderProps` to get the `match.url` as a return target for closing AssetList's modals.
					render = {routeRenderProps => <AssetList libraryId={libraryId} {...routeRenderProps} />}
				/>
				<Route path="/library/:libraryId/qr-designs/" exact component={QrCodesDesignsGrid} />
				<Route path="/library/:libraryId/qr-designs/new/" exact render={(routeRenderProps) => {
					let { libraryId } = routeRenderProps.match.params;
					return (
						<QrCodesDesignForm libraryId={libraryId} />
					);
				}} />
				<Route path="/library/:libraryId/qr-designs/:designId/" exact render={(routeRenderProps) => {
					let { libraryId, designId } = routeRenderProps.match.params;
					return (
						<QrCodesDesignForm libraryId={libraryId} designId={designId} />
					);
				}} />
				<Route path="/library/:libraryId/qr-designs/:designId/codes/" component={QrCodesGrid} />
				<Route path="/library/:libraryId/previews/" exact render={routeRenderProps => <PreviewGrid {...routeRenderProps} libraryId={libraryId} />} />
				{isContentFeedAssignmentFlowsEnabled && <Route path="/library/:libraryId/content-feeds/assign" exact render={routeRenderProps => <AssignContentFeedForm {...routeRenderProps} libraryId={libraryId} />} />}
				{isContentFeedAssignmentFlowsEnabled && <Route path="/library/:libraryId/content-feeds/assign-blank" exact render={routeRenderProps => <AssignBlankContentFeedForm {...routeRenderProps} libraryId={libraryId} />} />}
				{isContentFeedsTabEnabled && <Route path="/library/:libraryId/content-feeds/new" exact render={routeRenderProps => <CreateContentFeedForm {...routeRenderProps} libraryId={libraryId} />} />}
				<Route path="/library/:libraryId/content-feeds/:contentFeedId/updates-history/" exact component={UpdatesHistoryPage} />
				<Route path="/library/:libraryId/content-feeds/" render={routeRenderProps => <ContentFeedList {...routeRenderProps} libraryId={libraryId} />} />
				<Route path="/library/:libraryId/" render={routeRenderProps => <CreativeGrid {...routeRenderProps} libraryId={libraryId} />} />
			</Switch>
			<ApprovalHistoryModal />
			<ImportSocialCreativesModal />
		</div>
	);
}

CreativesList.displayName = "CreativesList";
